import { differenceInCalendarDays } from 'date-fns';
import { type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Typography } from '@/components/ui/typography';
import {
  formatInTimeZone,
  getRangeTimeZoneLabel,
  getTimeZoneGenericName,
  localDayAt,
} from '@/utils/time-zone';

import { TimeZoneChip, TimeZoneChipZone } from '../time-zone-chip';

export type GoalWindow = {
  start: { date: string; time: string };
  end: { date: string; time: string } | null;
  timeZoneLabel: string;
  /** The zone's name for every season. eg 'Central Time' */
  timeZoneName: string;
  totalDays: number | null;
};

/**
 * A hiring goal's start and end at the opening, the zone label for the days they
 * span, and how many of those days the goal runs.
 * @param startDate - The goal's start instant. eg '2026-09-01T05:00:00Z'
 * @param endAt - The instant the goal ends, resolved by the API; null when open-ended.
 * @param timeZone - The opening's IANA zone. eg 'America/Chicago'
 * @returns The window, or null for an unknown zone.
 */
export function getGoalWindow(
  startDate: string,
  endAt: string | null | undefined,
  timeZone: string,
): GoalWindow | null {
  const startAt = new Date(startDate);
  const start = formatInTimeZone(startAt, timeZone);
  const startDay = localDayAt(startAt, timeZone);
  if (!start || !startDay) return null;

  const endInstant = endAt ? new Date(endAt) : null;
  const end = endInstant ? formatInTimeZone(endInstant, timeZone) : null;
  const endDay = endInstant ? localDayAt(endInstant, timeZone) : null;
  // An open-ended goal has no last day, so no one abbreviation holds for all of it.
  const timeZoneLabel = endDay
    ? getRangeTimeZoneLabel(startDay, endDay, timeZone)
    : timeZone;
  if (!timeZoneLabel) return null;

  return {
    start,
    end,
    timeZoneLabel,
    timeZoneName: getTimeZoneGenericName(startAt, timeZone),
    totalDays: endDay ? differenceInCalendarDays(endDay, startDay) + 1 : null,
  };
}

export function GoalStamp({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Typography variant="caption">{label}</Typography>
      <Typography variant="body2" className="whitespace-nowrap">
        {children}
      </Typography>
    </div>
  );
}

export function GoalInstant({ date, time }: { date: string; time: string }) {
  return (
    <>
      {date} <span className="text-fx-neutral-600 font-normal">{time}</span>
    </>
  );
}

/**
 * A goal instant as tooltip copy writes it, where times read "9:04am" and the
 * stamps' "9:04 AM".
 * @param instant - The date and time at the opening. eg { date: 'Aug 24, 2026', time: '9:04 AM' }
 * @returns The instant for a tooltip. eg 'Aug 24, 2026 9:04am'
 */
export function tooltipInstant({ date, time }: { date: string; time: string }) {
  return `${date} ${time.replace(' ', '').toLowerCase()}`;
}

/**
 * The goal's time zone tooltip: where the opening is, and the window the goal
 * counts in. A goal with no end counts by day.
 */
export function GoalTimeZoneTooltipBody({
  goalWindow,
  locationName,
}: {
  goalWindow: GoalWindow;
  locationName?: string;
}) {
  return (
    <p>
      {locationName && (
        <>
          <FormattedMessage
            defaultMessage="This opening is in {location}."
            description="First sentence of the tooltip on a hiring goal's time zone chip. {location} is the opening's location name, such as Dallas, TX"
            values={{ location: locationName }}
          />{' '}
        </>
      )}
      {goalWindow.end ? (
        <FormattedMessage
          defaultMessage="This goal counts applicants, hires, and spend from {start} to {end} {timeZone}."
          description="Tooltip on a hiring goal's time zone chip. {start} and {end} are a date and time such as Aug 24, 2026 9:04am; {timeZone} is the zone's name such as Central Time"
          values={{
            start: tooltipInstant(goalWindow.start),
            end: tooltipInstant(goalWindow.end),
            timeZone: goalWindow.timeZoneName,
          }}
        />
      ) : (
        <FormattedMessage
          defaultMessage="This goal counts applicants, hires, and spend from {start} {timeZone}. It has no end date, so counts run through today."
          description="Tooltip on the time zone chip of a hiring goal with no end date. {start} is a date and time such as Aug 24, 2026 9:04am; {timeZone} is the zone's name such as Central Time"
          values={{
            start: tooltipInstant(goalWindow.start),
            timeZone: goalWindow.timeZoneName,
          }}
        />
      )}
    </p>
  );
}

/** The chip naming the zone a goal's dates and times are in. */
export function GoalTimeZoneChip({
  timeZone,
  timeZoneLabel,
  tooltipBody,
}: {
  timeZone: string;
  timeZoneLabel: string;
  /** Replaces the chip's default tooltip body. */
  tooltipBody?: ReactNode;
}) {
  return (
    <TimeZoneChip timeZone={timeZone} tooltipBody={tooltipBody}>
      <FormattedMessage
        defaultMessage="All times in <zone>{timeZone}</zone>"
        description="Chip below the hiring goal's dates. {timeZone} is an IANA zone with its abbreviation, such as America/Chicago (CDT)"
        values={{
          timeZone: timeZoneLabel,
          zone: chunks => <TimeZoneChipZone>{chunks}</TimeZoneChipZone>,
        }}
      />
    </TimeZoneChip>
  );
}

export function GoalStartsLabel() {
  return (
    <FormattedMessage
      defaultMessage="Starts"
      description="Caption above the hiring goal's start date and time"
    />
  );
}

export function GoalEndsLabel() {
  return (
    <FormattedMessage
      defaultMessage="Ends"
      description="Caption above the hiring goal's end date and time"
    />
  );
}
